import { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, FileText, Loader2, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type LessonReportRow,
  formatEpochDate,
  lessonWindowLabel,
} from "./types";
import { LessonGenerateDialog } from "./LessonGenerateDialog";

export function LessonReportList({
  lessonId,
  onBack,
  onSelectReport,
}: {
  lessonId: number;
  onBack: () => void;
  onSelectReport: (r: LessonReportRow) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [reports, setReports] = useState<LessonReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [lessonName, setLessonName] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [searching, setSearching] = useState(false);
  const [notFoundReason, setNotFoundReason] = useState<
    "not_found" | "no_data" | "failed" | null
  >(null);
  const [showGenerate, setShowGenerate] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const ctrl = new AbortController();

    const load = async () => {
      try {
        const [reportsRes, nameRes] = await Promise.all([
          fetch(`/api/internal/admin/lesson-reports?lessonId=${lessonId}`, {
            signal: ctrl.signal,
          }),
          fetch(`/api/internal/admin/lessons/${lessonId}/name`, {
            signal: ctrl.signal,
          }),
        ]);
        if (!reportsRes.ok) throw new Error(`${reportsRes.status}`);
        const data = (await reportsRes.json()) as LessonReportRow[];
        setReports(data);
        if (nameRes.ok) {
          const nameData = (await nameRes.json()) as { name: string | null };
          setLessonName(nameData.name);
        }
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => ctrl.abort();
  }, [lessonId, retryKey]);

  const handleDateSearch = async () => {
    setSearching(true);
    setNotFoundReason(null);
    try {
      const params = new URLSearchParams({
        lessonId: String(lessonId),
        startDate,
        endDate,
      });
      const r = await fetch(
        `/api/internal/admin/lesson-reports/lookup?${params}`,
      );
      if (r.status === 404) {
        setNotFoundReason("not_found");
        return;
      }
      const data = (await r.json()) as LessonReportRow;
      if (data.status === "no_data") {
        setNotFoundReason("no_data");
        return;
      }
      if (data.status === "failed") {
        setNotFoundReason("failed");
        return;
      }
      onSelectReport(data);
    } catch {
      setNotFoundReason("not_found");
    } finally {
      setSearching(false);
    }
  };

  const handleClickReport = async (report: LessonReportRow) => {
    const params = new URLSearchParams({
      lessonId: String(report.lesson_id),
      startDate: formatEpochDate(report.window_start),
      endDate: formatEpochDate(report.window_end),
    });
    const r = await fetch(
      `/api/internal/admin/lesson-reports/lookup?${params}`,
    );
    if (!r.ok) return;
    const data = (await r.json()) as LessonReportRow;
    onSelectReport(data);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h2 className="text-sm font-semibold text-foreground">
          {lessonName ?? `Lesson ${lessonId}`} — Reports
        </h2>
        <span className="text-xs text-muted-foreground">ID: {lessonId}</span>
      </div>

      <div className="rounded-xl border bg-card shadow-sm px-5 py-4 flex flex-col gap-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Search by date range
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setNotFoundReason(null);
              }}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setNotFoundReason(null);
              }}
              className="w-40"
            />
          </div>
          <Button
            onClick={handleDateSearch}
            disabled={searching}
            className="gap-2"
          >
            {searching ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Search className="size-4" />
            )}
            Search
          </Button>
        </div>
        {notFoundReason && (
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground">
              {notFoundReason === "no_data"
                ? `No sessions found for this date range when last checked. Want to try again?`
                : notFoundReason === "failed"
                  ? `Report generation failed for this date range. Want to try again?`
                  : `No report found for this date range.`}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7"
              onClick={() => setShowGenerate(true)}
            >
              {notFoundReason === "no_data" || notFoundReason === "failed"
                ? "Regenerate"
                : "Generate"}
            </Button>
          </div>
        )}
        {showGenerate && (
          <LessonGenerateDialog
            lessonId={lessonId}
            startDate={startDate}
            endDate={endDate}
            onClose={() => setShowGenerate(false)}
            onDone={(r) => {
              setShowGenerate(false);
              onSelectReport(r);
            }}
          />
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-xl border bg-card px-5 py-4 flex items-center gap-4"
            >
              <Skeleton className="size-9 rounded-lg" />
              <div className="flex-1 flex flex-col gap-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 px-1">
          <p className="text-sm text-destructive">Failed to load reports.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRetryKey((k) => k + 1)}
          >
            Retry
          </Button>
        </div>
      ) : reports.filter(
          (r) => r.status === "generated" || r.status === "stale",
        ).length === 0 ? (
        <p className="text-sm text-muted-foreground px-1">No reports yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {reports
            .filter((r) => r.status === "generated" || r.status === "stale")
            .map((report) => {
              const label = lessonWindowLabel(report);
              return (
                <button
                  key={report.id}
                  onClick={() => handleClickReport(report)}
                  className="w-full text-left rounded-xl border bg-card shadow-sm px-5 py-4 flex items-center gap-4 hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <FileText className="size-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">
                      {label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {report.timezone}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge
                      variant={
                        report.status === "generated" ? "default" : "secondary"
                      }
                      className="text-xs capitalize"
                    >
                      {report.status}
                    </Badge>
                    <ChevronRight className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
